import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Menu,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Toolbar,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  ArrowBack,
  ArrowForward,
  Delete as DeleteIcon,
  Refresh,
  Save as SaveIcon,
} from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { enumLabel } from '../i18n/translate';
import { whatsappTemplateApi } from '../services/api';
import {
  WHATSAPP_TEMPLATE_PLACEHOLDERS,
  WhatsAppTemplate,
  WhatsAppTemplateFormData,
  emptyWhatsAppTemplateFormData,
  mapWhatsAppTemplateToFormData,
} from '../types/whatsappTemplates';
import { DETAIL_FORM_UNIFORM_FIELDS_SX } from '../styles/detailFormStyles';

const WHATSAPP_TEMPLATES_QUERY_KEY = 'whatsappTemplates';
const PAGE_SIZE = 10;

export const WhatsAppTemplatesPage: React.FC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [page, setPage] = useState(0);
  const [isCreating, setIsCreating] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<WhatsAppTemplate | null>(null);
  const [formData, setFormData] = useState<WhatsAppTemplateFormData>(emptyWhatsAppTemplateFormData);
  const [deleteTarget, setDeleteTarget] = useState<WhatsAppTemplate | null>(null);
  const [placeholderMenuAnchor, setPlaceholderMenuAnchor] = useState<HTMLElement | null>(null);
  const bodyFieldRef = useRef<HTMLTextAreaElement | null>(null);

  const templatesQuery = useQuery<WhatsAppTemplate[]>(WHATSAPP_TEMPLATES_QUERY_KEY, () => whatsappTemplateApi.getAll());
  const templates = templatesQuery.data ?? [];
  const totalPages = Math.max(1, Math.ceil(templates.length / PAGE_SIZE));
  const paginatedTemplates = templates.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  useEffect(() => {
    if (page > 0 && page >= totalPages) {
      setPage(Math.max(0, totalPages - 1));
    }
  }, [page, totalPages]);

  const availablePlaceholders = useMemo(() => WHATSAPP_TEMPLATE_PLACEHOLDERS[formData.type], [formData.type]);

  const saveMutation = useMutation(
    () =>
      selectedTemplate
        ? whatsappTemplateApi.update(selectedTemplate.id, formData)
        : whatsappTemplateApi.create(formData),
    {
      onSuccess: (saved) => {
        queryClient.invalidateQueries(WHATSAPP_TEMPLATES_QUERY_KEY);
        toast.success(t('whatsappTemplates.messages.saved', 'Template saved'));
        setIsCreating(false);
        setSelectedTemplate(saved);
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || t('whatsappTemplates.messages.saveFailed', 'Failed to save template'));
      },
    }
  );

  const deleteMutation = useMutation((id: number) => whatsappTemplateApi.delete(id), {
    onSuccess: () => {
      queryClient.invalidateQueries(WHATSAPP_TEMPLATES_QUERY_KEY);
      toast.success(t('whatsappTemplates.messages.deleted', 'Template deleted'));
      setDeleteTarget(null);
      setSelectedTemplate(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || t('whatsappTemplates.messages.deleteFailed', 'Failed to delete template'));
    },
  });

  const handleStartCreate = () => {
    setSelectedTemplate(null);
    setFormData(emptyWhatsAppTemplateFormData);
    setIsCreating(true);
  };

  const handleSelectTemplate = (template: WhatsAppTemplate) => {
    setIsCreating(false);
    setSelectedTemplate(template);
    setFormData(mapWhatsAppTemplateToFormData(template));
  };

  const insertPlaceholder = (token: string) => {
    const textarea = bodyFieldRef.current;
    if (!textarea) {
      setFormData((prev) => ({ ...prev, body: `${prev.body}{{${token}}}` }));
      setPlaceholderMenuAnchor(null);
      return;
    }
    const start = textarea.selectionStart ?? formData.body.length;
    const end = textarea.selectionEnd ?? formData.body.length;
    const newValue = `${formData.body.slice(0, start)}{{${token}}}${formData.body.slice(end)}`;
    setFormData((prev) => ({ ...prev, body: newValue }));
    setPlaceholderMenuAnchor(null);
    requestAnimationFrame(() => {
      textarea.focus();
      const cursor = start + token.length + 4;
      textarea.setSelectionRange(cursor, cursor);
    });
  };

  const isFormValid = formData.name.trim() && formData.body.trim();

  return (
    <Box>
      <Box
        sx={{
          position: 'sticky',
          top: 64,
          zIndex: 1100,
          bgcolor: 'background.paper',
          borderBottom: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}
      >
        <Container maxWidth="xl" disableGutters>
          <Toolbar sx={{ pl: { xs: 2, sm: 3 }, pr: { xs: 2, sm: 3 }, minHeight: '48px !important', py: 0.5 }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleStartCreate}
                size="small"
                sx={{ textTransform: 'none' }}
              >
                {t('whatsappTemplates.newTemplate', 'New Template')}
              </Button>
              <Button
                variant="contained"
                startIcon={saveMutation.isLoading ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                onClick={() => saveMutation.mutate()}
                size="small"
                disabled={(!isCreating && !selectedTemplate) || !isFormValid || saveMutation.isLoading}
                sx={{ textTransform: 'none' }}
              >
                {t('common.save', 'Save')}
              </Button>
              <Button
                variant="contained"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={() => selectedTemplate && setDeleteTarget(selectedTemplate)}
                size="small"
                disabled={!selectedTemplate}
                sx={{ textTransform: 'none' }}
              >
                {t('common.delete', 'Delete')}
              </Button>
            </Box>
          </Toolbar>
        </Container>
      </Box>

      <Container maxWidth="xl" disableGutters sx={{ py: 1 }}>
        <Grid container spacing={0}>
          <Grid item xs={12}>
            <Paper elevation={1}>
              <Toolbar
                sx={{
                  pl: { xs: 2, sm: 3 },
                  pr: { xs: 2, sm: 3 },
                  minHeight: '48px !important',
                  py: 0.5,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    startIcon={<Refresh fontSize="small" />}
                    size="small"
                    onClick={() => templatesQuery.refetch()}
                    sx={{ textTransform: 'none' }}
                  >
                    {t('common.refresh', 'Refresh')}
                  </Button>
                </Box>

                <Box sx={{ flexGrow: 1 }} />

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <IconButton size="small" onClick={() => setPage((prev) => Math.max(0, prev - 1))} disabled={page === 0}>
                    <ArrowBack fontSize="small" />
                  </IconButton>
                  <Typography variant="body2" color="text.secondary">
                    {templates.length === 0 ? '0' : page * PAGE_SIZE + 1}-{Math.min((page + 1) * PAGE_SIZE, templates.length)} {t('common.of', 'of')} {templates.length}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => setPage((prev) => Math.min(totalPages - 1, prev + 1))}
                    disabled={page >= totalPages - 1}
                  >
                    <ArrowForward fontSize="small" />
                  </IconButton>
                </Box>
              </Toolbar>

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('whatsappTemplates.columns.name', 'Name')}</TableCell>
                      <TableCell>{t('whatsappTemplates.columns.type', 'Type')}</TableCell>
                      <TableCell align="center">{t('whatsappTemplates.columns.active', 'Active')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {templatesQuery.isLoading ? (
                      <TableRow>
                        <TableCell colSpan={3} align="center" sx={{ py: 3 }}>
                          <CircularProgress size={24} />
                        </TableCell>
                      </TableRow>
                    ) : paginatedTemplates.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={3} align="center" sx={{ py: 3 }}>
                          <Typography variant="body2" color="text.secondary">
                            {t('whatsappTemplates.empty', 'No WhatsApp templates yet. Create one to start messaging customers from Rentals.')}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedTemplates.map((template) => (
                        <TableRow
                          key={template.id}
                          hover
                          selected={selectedTemplate?.id === template.id}
                          onClick={() => handleSelectTemplate(template)}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell>{template.name}</TableCell>
                          <TableCell>
                            <Chip size="small" label={enumLabel(t, 'whatsappTemplates.types', template.type)} />
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              size="small"
                              color={template.active ? 'success' : 'default'}
                              label={template.active ? t('common.active', 'Active') : t('common.inactive', 'Inactive')}
                            />
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>

          {(isCreating || selectedTemplate) && (
            <Grid item xs={12}>
              <Paper elevation={1} sx={{ mt: 2, p: 2, ...DETAIL_FORM_UNIFORM_FIELDS_SX }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  {isCreating ? t('whatsappTemplates.newTemplate', 'New Template') : selectedTemplate?.name}
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label={t('whatsappTemplates.fields.name', 'Name')}
                      value={formData.name}
                      onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      select
                      size="small"
                      label={t('whatsappTemplates.fields.type', 'Type')}
                      value={formData.type}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, type: e.target.value as WhatsAppTemplateFormData['type'] }))
                      }
                    >
                      <MenuItem value="ORDER_CONFIRMATION">{enumLabel(t, 'whatsappTemplates.types', 'ORDER_CONFIRMATION')}</MenuItem>
                      <MenuItem value="INVOICE">{enumLabel(t, 'whatsappTemplates.types', 'INVOICE')}</MenuItem>
                      <MenuItem value="STATUS_UPDATE">{enumLabel(t, 'whatsappTemplates.types', 'STATUS_UPDATE')}</MenuItem>
                    </TextField>
                  </Grid>

                  <Grid item xs={12}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        {t('whatsappTemplates.fields.body', 'Message')}
                      </Typography>
                      <Button size="small" onClick={(e) => setPlaceholderMenuAnchor(e.currentTarget)}>
                        {t('whatsappTemplates.insertPlaceholder', 'Insert Placeholder')}
                      </Button>
                      <Menu
                        anchorEl={placeholderMenuAnchor}
                        open={Boolean(placeholderMenuAnchor)}
                        onClose={() => setPlaceholderMenuAnchor(null)}
                      >
                        {availablePlaceholders.map((token) => (
                          <MenuItem key={token} onClick={() => insertPlaceholder(token)}>
                            {`{{${token}}}`}
                          </MenuItem>
                        ))}
                      </Menu>
                    </Stack>
                    <TextField
                      fullWidth
                      multiline
                      minRows={8}
                      inputRef={bodyFieldRef}
                      value={formData.body}
                      onChange={(e) => setFormData((prev) => ({ ...prev, body: e.target.value }))}
                      placeholder={t(
                        'whatsappTemplates.bodyPlaceholder',
                        'Hi {{customerName}}, thank you for your reservation ({{reservationNumber}})...'
                      )}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.active}
                          onChange={(e) => setFormData((prev) => ({ ...prev, active: e.target.checked }))}
                        />
                      }
                      label={t('common.active', 'Active')}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          )}
        </Grid>
      </Container>

      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>{t('whatsappTemplates.deleteTitle', 'Delete Template')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('whatsappTemplates.deleteConfirm', 'Delete "{{name}}"? This cannot be undone.', {
              name: deleteTarget?.name,
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>{t('common.cancel', 'Cancel')}</Button>
          <Button
            color="error"
            variant="contained"
            disabled={deleteMutation.isLoading}
            onClick={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
          >
            {deleteMutation.isLoading ? <CircularProgress size={20} /> : t('common.delete', 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
